import _ from 'lodash';
import { useState } from 'react';

interface IUseAggregateBooleanReturnValue {
  /** The aggregate value of all boolean values in the collection. */
  aggregateValue: boolean;

  /** The collection of boolean values. */
  booleanValues: Record<number | string, boolean>;

  /**
   * Sets the value of the boolean corresponding to the specified `id` in the collection.
   * If the `id` does not exist in the collection, a key-value pair is added to the collection.
   */
  setBooleanValueById: (id: string | number, value: boolean) => void;
}

/**
 * Aggregates a collection of boolean values into a single boolean value.
 * Each item in the collection consists of a key-value pair where the key is a unique identifier and the value is a boolean.
 *
 * If no items are provided, it returns `true`.
 *
 * @example
 * const { aggregateValue, setBooleanValueById } = useAggregateBoolean({
 *   a: true,
 *   b: false,
 *   c: true,
 * });
 *
 * setBooleanValueById('b', true);
 *
 * console.log(aggregateValue); // true
 */
export const useAggregateBoolean = (
  initialValues: Record<number | string, boolean> = {}
): IUseAggregateBooleanReturnValue => {
  const [booleanValues, setBooleanValues] =
    useState<Record<number | string, boolean>>(initialValues);

  const setBooleanValueById = (key: string | number, value: boolean) => {
    setBooleanValues((prevValues) => ({ ...prevValues, [key]: value }));
  };

  return {
    aggregateValue: _.every(booleanValues, (value) => value),
    booleanValues,
    setBooleanValueById,
  };
};
